import {useEffect, useMemo, useState} from "react";
import {Drawer, Loader, Modal, SegmentedControl, Text} from "@mantine/core";
import {useDisclosure, useMediaQuery} from "@mantine/hooks";
import slugify from "@sindresorhus/slugify";
import {useLocation, useNavigate} from "react-router-dom";
import {t} from "i18next";
import {dibApi} from "../chat/dibApi";
import {ResourceError} from "./ResourceError";
import type {ChecklistData, ChecklistFilterGroup, Status} from "./types";
import layoutClasses from "./ContentLayout.module.css";
import classes from "./checklist/checklist.module.css";

import {
    ChecklistStructureProvider,
    ChecklistInteractionProvider,
    type ChecklistStructureValue,
    type ChecklistInteractionValue,
} from "./checklist/ChecklistDataContext";
import {
    useChecklistState,
    ChecklistEventTypes,
} from "./checklist/hooks/useChecklistState";
import {useChecklistModals} from "./checklist/hooks/useChecklistModals";
import {useChecklistScrollSpy} from "./checklist/hooks/useChecklistScrollSpy";
import {
    getRelatedDocuments,
    getRelatedDocumentsOrder,
    getChecklistFilterGroups,
} from "./checklist/utils";
import {useViewFiltering} from "./checklist/hooks/useViewFiltering";
import {useChecklistVisibility} from "./checklist/hooks/useChecklistVisibility";
import {useChecklistActions} from "./checklist/hooks/useChecklistActions";
import {useCSSHighlights} from "../../useCssHighlights";

import ChecklistToc from "./checklist/ChecklistToc";
import {ChecklistTree} from "./checklist/ChecklistTree";
import {ChecklistConfig} from "./checklist/ChecklistConfig";
import {ChecklistControls} from "./checklist/ChecklistControls";
import {ChecklistModals} from "./checklist/ChecklistModals";
import {DocumentsList} from "./checklist/DocumentsList";
import {GenerateButtons} from "./checklist/GenerateButtons";
import {CommentProvider} from "./checklist/comments/CommentContext";
import {CommentPanel} from "./checklist/comments/CommentPanel";
import {DocumentView, DOCUMENT_GENERATOR_PLACEHOLDER_ID} from "./checklist/document-generator/DocumentView";

// ---------------------------------------------------------------------------
// Outer component: data fetching + loading/error handling
// ---------------------------------------------------------------------------

export const ChecklistResource = ({
    resourceId,
    collectionId,
    dokumentId,
    isPreview,
}: {
    resourceId: string;
    collectionId?: string;
    dokumentId?: string;
    isPreview?: boolean;
}) => {
    const {data, isLoading, isError, error} =
        dibApi.endpoints.getChecklist.useQuery({
            topicId: resourceId,
            isPreview,
        });

    if (isLoading) {
        return (
            <div className={layoutClasses.statusMessage}>
                <Loader color="gneis.5" size="md" />
            </div>
        );
    }

    if (isError) {
        return <ResourceError error={error} />;
    }

    if (!data) return null;

    return (
        <ChecklistView
            checklistData={data}
            collectionId={collectionId}
            dokumentId={dokumentId}
            isPreview={isPreview ?? false}
        />
    );
};

// ---------------------------------------------------------------------------
// Inner component: the full orchestrator
// ---------------------------------------------------------------------------

const ChecklistView = ({
    checklistData,
    collectionId,
    dokumentId,
    isPreview,
}: {
    checklistData: ChecklistData;
    collectionId?: string;
    dokumentId?: string;
    isPreview: boolean;
}) => {
    // -----------------------------------------------------------------------
    // Step 2: Collection data queries
    // -----------------------------------------------------------------------

    const {data: collectionData} = dibApi.endpoints.getCollectionData.useQuery(
        {
            topicId: checklistData.metadata.topicId,
            resourceId: checklistData.metadata.id,
            collectionId: collectionId ?? "",
        },
        {skip: !collectionId}
    );

    const {data: defaultCollectionData} =
        dibApi.endpoints.getCollectionData.useQuery(
            {
                topicId: checklistData.metadata.topicId,
                resourceId: checklistData.metadata.id,
                collectionId: checklistData.defaultCollectionId ?? "",
            },
            {
                skip: !checklistData.defaultCollectionId || !!collectionId,
            }
        );

    // -----------------------------------------------------------------------
    // Step 3: Mutation hooks
    // -----------------------------------------------------------------------

    const [patchDimensions, {isLoading: isPatchDimensionsLoading}] =
        dibApi.endpoints.patchDimensions.useMutation();
    const [patchCollectionName, {isLoading: isPatchCollectionNameLoading}] =
        dibApi.endpoints.patchCollectionName.useMutation();
    const [patchStatusForCheckItems] =
        dibApi.endpoints.patchStatusForCheckItems.useMutation();
    const [patchDocumentSelected] =
        dibApi.endpoints.patchDocumentSelected.useMutation();
    const [getChecklistPartElements] =
        dibApi.endpoints.getChecklistPartElements.useLazyQuery();

    // -----------------------------------------------------------------------
    // Step 4: Comments query (seed RTK cache)
    // -----------------------------------------------------------------------

    const {data: commentsData} = dibApi.endpoints.getComments.useQuery(
        {
            topicId: checklistData.metadata.topicId,
            collectionId: collectionId ?? "",
        },
        {skip: !collectionId}
    );

    // -----------------------------------------------------------------------
    // Step 5: useChecklistState hook
    // -----------------------------------------------------------------------

    const {state, dispatch, fetchItem, fetchAll} = useChecklistState({
        elements: checklistData.elements,
        objects: checklistData.objects,
        isPreview,
        resourceId: checklistData.metadata.id,
        getChecklistPartElements: async (params) =>
            getChecklistPartElements(params).unwrap(),
    });

    // -----------------------------------------------------------------------
    // Step 6: useChecklistModals hook
    // -----------------------------------------------------------------------

    const {
        isEditFillingOpen,
        isCreateFillingOpen,
        isOpenFillingOpen,
        isShareFillingOpen,
        isDuplicateFillingOpen,
        isDeleteFillingOpen,
        confirmCheckAllData,
        openEditFilling,
        openCreateFilling,
        openOpenFilling,
        openShareFilling,
        openDuplicateFilling,
        openDeleteFilling,
        openConfirmCheckAll,
        closeModal,
    } = useChecklistModals();

    // -----------------------------------------------------------------------
    // Step 7: Derived state
    // -----------------------------------------------------------------------

    const {labelData, viewFilteredToc, effectiveRootNodeId} = useViewFiltering(
        checklistData,
        collectionData,
        defaultCollectionData
    );

    const {
        hiddenIds,
        searchHitIds,
        statusMap,
        infoIdSet,
        hasOnlyInfoNodes,
        collectionProgress,
        allItemsExpanded,
    } = useChecklistVisibility({
        viewFilteredToc,
        effectiveRootNodeId,
        statusFilterSelected: state.filter.statusFilterSelected,
        searchTerm: state.searchTerm,
        elements: state.elements,
        openItems: state.openItems,
        collectionData,
        commentsData,
        checklistData,
    });

    const isDraft =
        !collectionData ||
        collectionData.metadata.collectionId === undefined ||
        collectionData.metadata.collectionId ===
            checklistData.defaultCollectionId;

    const collectionAccess = collectionData?.access ?? true;

    const activeFilters: ChecklistFilterGroup[] = getChecklistFilterGroups(
        state.filter.statusFilterSelected,
        checklistData.objects ?? {}
    );

    const relatedDocuments = useMemo(
        () => getRelatedDocuments(viewFilteredToc, effectiveRootNodeId),
        [viewFilteredToc, effectiveRootNodeId]
    );
    // TODO: used by upcoming document ordering feature
    const _relatedDocumentsOrder = useMemo(
        () => getRelatedDocumentsOrder(viewFilteredToc, effectiveRootNodeId),
        [viewFilteredToc, effectiveRootNodeId]
    );

    // -----------------------------------------------------------------------
    // Step 8: Scroll spy
    // -----------------------------------------------------------------------

    const {
        containerRef,
        tocContainerRef,
        locked,
        manuallySetActiveElementId,
        observeElement,
        unobserveElement,
        scrollToCheckItem,
        scrollToActiveTocItem,
        setActiveTocItem,
    } = useChecklistScrollSpy(checklistData.tocParentMap);

    // -----------------------------------------------------------------------
    // Step 9: CSS highlights
    // -----------------------------------------------------------------------

    useCSSHighlights(state.searchTerm, "[data-searchable]", [
        state.searchTerm,
        state.fetchedAll,
        viewFilteredToc,
        state.openItems,
    ]);

    // -----------------------------------------------------------------------
    // Step 10: Actions
    // -----------------------------------------------------------------------

    const actions = useChecklistActions({
        checklistData,
        collectionData,
        viewFilteredToc,
        effectiveRootNodeId,
        labelData,
        hiddenIds,
        allItemsExpanded,
        state,
        dispatch,
        fetchItem,
        fetchAll,
        scrollSpy: {
            containerRef,
            tocContainerRef,
            locked,
            manuallySetActiveElementId,
            scrollToCheckItem,
            scrollToActiveTocItem,
            setActiveTocItem,
        },
        mutations: {
            patchDimensions: (params) => patchDimensions(params).unwrap(),
            patchCollectionName: (params) =>
                patchCollectionName(params).unwrap(),
            patchStatusForCheckItems,
            patchDocumentSelected,
        },
        modals: {
            openCreateFilling,
            openConfirmCheckAll,
            closeModal,
            confirmCheckAllData,
        },
    });

    // -----------------------------------------------------------------------
    // Step 11: Tab switching
    // -----------------------------------------------------------------------

    const [activeTab, setActiveTab] = useState<"checklist" | "documents">(
        dokumentId ? "documents" : "checklist"
    );

    useEffect(() => {
        setActiveTab(dokumentId ? "documents" : "checklist");
    }, [dokumentId]);

    const isDocGenTabHidden = Object.keys(relatedDocuments).length === 0;

    const effectiveTab = useMemo(() => {
        if (hasOnlyInfoNodes && !isDocGenTabHidden) return "documents";
        if (!hasOnlyInfoNodes && isDocGenTabHidden) return "checklist";
        return activeTab;
    }, [activeTab, hasOnlyInfoNodes, isDocGenTabHidden]);

    const shouldRenderDocumentView = effectiveTab === "documents";

    // ── Tab navigation (URL-driven, matching DibApp2020) ────────────

    const navigate = useNavigate();
    const location = useLocation();

    const checklistBasePath = useMemo(() => {
        const parts = location.pathname.split("/");
        return parts.slice(0, 4).join("/");
    }, [location.pathname]);

    const collectionSuffix = collectionData?.metadata?.collectionId
        ? `/utfylling/${slugify(collectionData.metadata.name ?? "utfylling")}/${collectionData.metadata.collectionId}`
        : "";

    const handleTabChange = (val: string) => {
        if (val === "documents") {
            navigate(`${checklistBasePath}/dokument/n/i${collectionSuffix}`);
        } else {
            navigate(`${checklistBasePath}${collectionSuffix}`);
        }
    };

    // -----------------------------------------------------------------------
    // Step 11b: Mobile sidebar drawer
    // -----------------------------------------------------------------------

    const isMobile = useMediaQuery("(max-width: 991px)");
    const [drawerOpened, {open: openDrawer, close: closeDrawer}] =
        useDisclosure(false);

    // -----------------------------------------------------------------------
    // Step 12: Context assembly
    // -----------------------------------------------------------------------

    const checklistConfig = useMemo(
        () => ({
            metadata: checklistData.metadata,
            viewGroups: checklistData.viewGroups ?? [],
            labelData,
            defaultCollectionId: checklistData.defaultCollectionId,
        }),
        [
            checklistData.metadata,
            checklistData.viewGroups,
            checklistData.defaultCollectionId,
            labelData,
        ]
    );

    const structureCtx = useMemo<ChecklistStructureValue>(
        () => ({
            collectionData,
            isDraft,
            viewFilteredToc,
            effectiveRootNodeId,
            infoIdSet,
            checklistConfig,
        }),
        [
            collectionData,
            isDraft,
            viewFilteredToc,
            effectiveRootNodeId,
            infoIdSet,
            checklistConfig,
        ]
    );

    const interactionCtx = useMemo<ChecklistInteractionValue>(
        () => ({
            isHidden: (id: string) => hiddenIds.has(id),
            isSearchHit: (id: string) => searchHitIds.has(id),
            getStatus: (id: string): Status | -1 => statusMap[id] ?? -1,
            itemsState: {
                loadingState: state.items,
                elements: state.elements,
                objects: state.objects,
                openItems: state.openItems,
                fetchingAll: state.fetchingAll,
            },
            searchTerm: state.searchTerm,
            observeChecklistItemRef: observeElement,
            unobserveChecklistItemRef: unobserveElement,
            onClickItem: actions.handleClickChecklistItem,
            onClickTocItem: actions.handleClickTocItem,
            onCheckItemStatusClick: actions.handleCheckItemStatusClick,
        }),
        // Functions (observeElement, unobserveElement, handlers) are intentionally
        // omitted: observer functions are stable by design, and handlers are
        // plain closures captured at memo time.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            hiddenIds,
            searchHitIds,
            statusMap,
            state.items,
            state.elements,
            state.objects,
            state.openItems,
            state.fetchingAll,
            state.searchTerm,
        ]
    );

    // -----------------------------------------------------------------------
    // Step 14: Layout and rendering
    // -----------------------------------------------------------------------

    return (
        <ChecklistStructureProvider value={structureCtx}>
            <ChecklistInteractionProvider value={interactionCtx}>
                <CommentProvider>
                    <div className={classes.checklistWrapper}>
                        <button
                            type="button"
                            className={classes.drawerTrigger}
                            onClick={openDrawer}
                        >
                            ☰ {t("Action.OpenMenu")}
                        </button>

                        {isMobile ? (
                            <Drawer
                                opened={drawerOpened}
                                onClose={closeDrawer}
                                title={checklistConfig.metadata.name}
                                position="left"
                                size="sm"
                            >
                                <ChecklistConfig
                                    collectionData={collectionData}
                                    checklistConfig={checklistConfig}
                                    onPatchDimensions={
                                        actions.handlePatchDimensions
                                    }
                                    progress={collectionProgress}
                                    setEditFillingOpen={() =>
                                        openEditFilling()
                                    }
                                    setCreateFillingOpen={() =>
                                        openCreateFilling()
                                    }
                                    setOpenFillingOpen={() =>
                                        openOpenFilling()
                                    }
                                    setShareFillingOpen={() =>
                                        openShareFilling()
                                    }
                                    setDuplicateFillingOpen={() =>
                                        openDuplicateFilling()
                                    }
                                    setDeleteFillingOpen={() =>
                                        openDeleteFilling()
                                    }
                                />
                                {!shouldRenderDocumentView && (
                                    <ChecklistToc
                                        nodeId={effectiveRootNodeId}
                                        isRoot
                                    />
                                )}
                                <GenerateButtons
                                    resourceId={checklistData.metadata.id}
                                    collectionId={
                                        collectionData?.metadata?.collectionId
                                    }
                                />
                            </Drawer>
                        ) : (
                            <ChecklistConfig
                                className={classes.checklistConfig}
                                collectionData={collectionData}
                                checklistConfig={checklistConfig}
                                onPatchDimensions={
                                    actions.handlePatchDimensions
                                }
                                progress={collectionProgress}
                                setEditFillingOpen={() => openEditFilling()}
                                setCreateFillingOpen={() =>
                                    openCreateFilling()
                                }
                                setOpenFillingOpen={() => openOpenFilling()}
                                setShareFillingOpen={() =>
                                    openShareFilling()
                                }
                                setDuplicateFillingOpen={() =>
                                    openDuplicateFilling()
                                }
                                setDeleteFillingOpen={() =>
                                    openDeleteFilling()
                                }
                            />
                        )}

                        {!shouldRenderDocumentView && (
                            <ChecklistControls
                                key={
                                    collectionData?.metadata?.collectionId ??
                                    "draft"
                                }
                                allItemsExpanded={allItemsExpanded}
                                hasCollection={!!collectionData}
                                searchTerm={state.searchTerm}
                                filterState={{
                                    statusFilterSelected:
                                        state.filter.statusFilterSelected,
                                    showGroups: checklistData.showGroups ?? [],
                                    resetKey: state.filter.resetKey,
                                }}
                                onExpandAll={actions.handleExpandAll}
                                onSearchChange={actions.handleSearchChange}
                                onStatusToggle={actions.handleStatusToggle}
                            />
                        )}

                        {!isDocGenTabHidden && !hasOnlyInfoNodes && (
                            <SegmentedControl
                                value={effectiveTab}
                                onChange={handleTabChange}
                                data={[
                                    {
                                        label: t("Word.Checklist"),
                                        value: "checklist",
                                    },
                                    {
                                        label: t("Word.Templates"),
                                        value: "documents",
                                    },
                                ]}
                                style={{gridColumn: "1 / -1"}}
                            />
                        )}

                        <div className={classes.checklistGenerateButtons}>
                            <GenerateButtons
                                resourceId={checklistData.metadata.id}
                                collectionId={
                                    collectionData?.metadata?.collectionId
                                }
                            />
                        </div>

                        <div
                            className={classes.checklistTocWrapper}
                            ref={tocContainerRef}
                        >
                            {shouldRenderDocumentView ? (
                                <DocumentsList
                                    collectionData={collectionData}
                                    viewFilteredToc={viewFilteredToc}
                                    effectiveRootNodeId={effectiveRootNodeId}
                                    checklistConfig={checklistConfig}
                                    onToggleDocumentSelected={
                                        actions.handleToggleDocumentSelected
                                    }
                                    activeDocumentId={dokumentId}
                                />
                            ) : (
                                <ChecklistToc
                                    nodeId={effectiveRootNodeId}
                                    isRoot
                                />
                            )}
                        </div>

                        {shouldRenderDocumentView ? (
                            <div
                                className={`${classes.checklistItems} ${classes.documentPane ?? ""}`}
                            >
                                <DocumentView
                                    documentId={dokumentId ?? DOCUMENT_GENERATOR_PLACEHOLDER_ID}
                                    collectionId={
                                        collectionData?.metadata
                                            ?.collectionId
                                    }
                                    isDraft={isDraft}
                                    onPromptNewFilling={openCreateFilling}
                                />
                            </div>
                        ) : (
                            <div
                                className={classes.checklistItems}
                                ref={containerRef}
                            >
                                <div className={classes.checklistDescription}>
                                    <h1>{checklistData.metadata.name}</h1>
                                </div>
                                <ChecklistTree
                                    activeFilters={activeFilters}
                                    onClearSearchAndFilters={
                                        actions.handleClearSearchAndFilters
                                    }
                                />
                            </div>
                        )}
                    </div>

                    <CommentPanel
                        topicId={checklistData.metadata.topicId}
                        collectionId={
                            collectionData?.metadata?.collectionId ?? ""
                        }
                        viewFilteredToc={viewFilteredToc}
                        effectiveRootNodeId={effectiveRootNodeId}
                    />

                    <ChecklistModals
                        collectionData={collectionData}
                        viewFilteredToc={viewFilteredToc}
                        effectiveRootNodeId={effectiveRootNodeId}
                        checklistConfig={checklistConfig}
                        isEditFillingOpen={isEditFillingOpen}
                        isCreateFillingOpen={isCreateFillingOpen}
                        isOpenFillingOpen={isOpenFillingOpen}
                        isShareFillingOpen={isShareFillingOpen}
                        isDuplicateFillingOpen={isDuplicateFillingOpen}
                        isDeleteFillingOpen={isDeleteFillingOpen}
                        confirmCheckAllData={confirmCheckAllData}
                        infoIds={checklistData.infoIds ?? []}
                        leafNodeIds={checklistData.leafNodeIds ?? []}
                        closeModal={closeModal}
                        handleConfirmCheckAll={actions.handleConfirmCheckAll}
                        handlePatchDimensions={actions.handlePatchDimensions}
                        handlePatchCollectionName={
                            actions.handlePatchCollectionName
                        }
                        onCollectionChange={() =>
                            dispatch({
                                type: ChecklistEventTypes.RESET_STATUS,
                            })
                        }
                        isPatchCollectionNameLoading={
                            isPatchCollectionNameLoading
                        }
                        isPatchDimensionsLoading={isPatchDimensionsLoading}
                    />

                    {collectionAccess === false && (
                        <Modal
                            opened
                            onClose={() => {}}
                            withCloseButton={false}
                        >
                            <Text>{t("Error.NoFillingAccess")}</Text>
                        </Modal>
                    )}
                </CommentProvider>
            </ChecklistInteractionProvider>
        </ChecklistStructureProvider>
    );
};
